import { ref, computed } from 'vue';
import { onAuthStateChanged } from 'firebase/auth';
import { adminService } from '../firebase/services/adminService';
import { auth } from '../firebase/config';
import { 
  AdminUser, 
  AdminPermission, 
  UserRole,
  type AdminLoginCredentials as SchemaAdminLoginCredentials,
  type AdminAuthResult as SchemaAdminAuthResult
} from '../firebase/schema';

// Re-export types for backward compatibility
export type AdminLoginCredentials = SchemaAdminLoginCredentials;
export type AdminAuthResult = SchemaAdminAuthResult;

// Global admin state
const currentAdminUser = ref<AdminUser | null>(null);
const isAdminLoading = ref(false);
const adminError = ref<string | null>(null);

// Set up persistent auth state listener
let authStateListenerInitialized = false;

const initializeAuthStateListener = () => {
  if (authStateListenerInitialized) return;
  
  authStateListenerInitialized = true;
  
  onAuthStateChanged(auth, async (user) => {
    if (user) {
      // User is authenticated, check if they have admin privileges
      try {
        const adminUser = await adminService.verifyAdminUser(user.uid);
        currentAdminUser.value = adminUser;
      } catch (error) {
        console.error('Failed to verify admin status:', error);
        currentAdminUser.value = null;
      }
    } else {
      // User is not authenticated, clear admin state
      currentAdminUser.value = null;
      adminError.value = null;
    }
  });
};

export function useAdmin() {
  // Initialize auth state listener on first use
  initializeAuthStateListener();
  
  // Computed properties
  const isAdmin = computed(() => !!currentAdminUser.value);
  const isSuperAdmin = computed(() => 
    currentAdminUser.value?.role === UserRole.SUPER_ADMIN
  );
  
  const hasPermission = computed(() => (permission: AdminPermission) => {
    if (!currentAdminUser.value) return false;
    if (currentAdminUser.value.role === UserRole.SUPER_ADMIN) return true;
    return currentAdminUser.value.permissions.includes(permission);
  });

  // Admin authentication method
  const adminLogin = async (credentials: AdminLoginCredentials): Promise<AdminAuthResult> => {
    try {
      adminError.value = null;
      isAdminLoading.value = true;
      
      if (!adminService.validateAdminCredentials(credentials)) {
        throw new Error('Invalid admin credentials format');
      }

      // Use authenticateAdminUser instead of adminLogin
      const result = await adminService.authenticateAdminUser(credentials);
      // Ensure the user object has all required fields
      currentAdminUser.value = {
        ...result.user,
        id: result.user.userId, // Map userId to id for backward compatibility
        preferences: result.user.preferences || { preferredResolution: '', adConsent: false, theme: 'light' },
        role: result.user.role || UserRole.ADMIN
      };
      
      return result;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Admin login failed';
      adminError.value = errorMessage;
      throw error;
    } finally {
      isAdminLoading.value = false;
    }
  };

  const verifyAdminStatus = async (): Promise<boolean> => {
    try {
      adminError.value = null;
      isAdminLoading.value = true;
      
      const user = auth.currentUser;
      if (!user) {
        currentAdminUser.value = null;
        return false;
      }
      
      const adminUser = await adminService.verifyAdminUser(user.uid);
      if (adminUser) {
        currentAdminUser.value = {
          ...adminUser,
          id: adminUser.userId, // Map userId to id for backward compatibility
          preferences: adminUser.preferences || { preferredResolution: '', adConsent: false, theme: 'light' },
          role: adminUser.role || UserRole.ADMIN
        };
        return true;
      }
      
      currentAdminUser.value = null;
      return false;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Admin verification failed';
      adminError.value = errorMessage;
      currentAdminUser.value = null;
      return false;
    } finally {
      isAdminLoading.value = false;
    }
  };

  // Admin management methods
  const promoteToAdmin = async (
    targetUserId: string, 
    role: UserRole.ADMIN | UserRole.SUPER_ADMIN,
    permissions: AdminPermission[]
  ): Promise<void> => {
    if (!currentAdminUser.value) {
      throw new Error('Admin authentication required');
    }

    try {
      adminError.value = null;
      isAdminLoading.value = true;
      
      // Use userId instead of id for the current admin user
      const currentUserId = currentAdminUser.value.userId || currentAdminUser.value.id;
      
      await adminService.promoteToAdmin(
        currentUserId, 
        targetUserId, 
        role, 
        permissions
      );
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to promote user to admin';
      adminError.value = errorMessage;
      throw error;
    } finally {
      isAdminLoading.value = false;
    }
  };

  const revokeAdminAccess = async (targetUserId: string): Promise<void> => {
    if (!currentAdminUser.value) {
      throw new Error('Admin authentication required');
    }

    try {
      adminError.value = null;
      isAdminLoading.value = true;
      
      // Use userId instead of id for the current admin user
      const currentUserId = currentAdminUser.value.userId || currentAdminUser.value.id;
      
      await adminService.revokeAdminAccess(currentUserId, targetUserId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to revoke admin access';
      adminError.value = errorMessage;
      throw error;
    } finally {
      isAdminLoading.value = false;
    }
  };

  const updateAdminPermissions = async (
    targetUserId: string, 
    permissions: AdminPermission[]
  ): Promise<void> => {
    if (!currentAdminUser.value) {
      throw new Error('Admin authentication required');
    }

    try {
      adminError.value = null;
      isAdminLoading.value = true;
      
      // Use userId instead of id for the current admin user
      const currentUserId = currentAdminUser.value.userId || currentAdminUser.value.id;
      
      await adminService.updateAdminPermissions(
        currentUserId, 
        targetUserId, 
        permissions
      );
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update admin permissions';
      adminError.value = errorMessage;
      throw error;
    } finally {
      isAdminLoading.value = false;
    }
  };

  const getAllAdmins = async (): Promise<AdminUser[]> => {
    try {
      adminError.value = null;
      isAdminLoading.value = true;
      
      return await adminService.getAllAdmins();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch admin users';
      adminError.value = errorMessage;
      throw error;
    } finally {
      isAdminLoading.value = false;
    }
  };

  // Permission checking methods
  const checkPermission = async (permission: AdminPermission): Promise<boolean> => {
    if (!currentAdminUser.value) return false;
    
    try {
      // Use userId instead of id for the current admin user
      const currentUserId = currentAdminUser.value.userId || currentAdminUser.value.id;
      return await adminService.hasPermission(currentUserId, permission);
    } catch (error) {
      console.error('Error checking permission:', error);
      return false;
    }
  };

  const requirePermission = (permission: AdminPermission): void => {
    if (!hasPermission.value(permission)) {
      throw new Error(`Admin permission required: ${permission}`);
    }
  };

  // Utility methods
  const clearAdminError = (): void => {
    adminError.value = null;
  };

  const signOutAdmin = (): void => {
    currentAdminUser.value = null;
    adminError.value = null;
  };

  // Permission constants for easy access
  const PERMISSIONS = AdminPermission;
  const ROLES = UserRole;

  return {
    // State
    currentAdminUser: computed(() => currentAdminUser.value),
    isAdminLoading: computed(() => isAdminLoading.value),
    adminError: computed(() => adminError.value),
    
    // Computed
    isAdmin,
    isSuperAdmin,
    hasPermission,
    
    // Authentication
    adminLogin,
    verifyAdminStatus,
    signOutAdmin,
    
    // Admin management
    promoteToAdmin,
    revokeAdminAccess,
    updateAdminPermissions,
    getAllAdmins,
    
    // Permission checking
    checkPermission,
    requirePermission,
    
    // Utilities
    clearAdminError,
    
    // Constants
    PERMISSIONS,
    ROLES
  };
}

// Global admin initialization
export async function initializeAdminAuth() {
  return new Promise<void>((resolve) => {
    // Initialize the auth state listener by calling useAdmin
    useAdmin();
    
    // Wait for Firebase Auth to restore any existing session
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // User is authenticated, verify admin status
          const adminUser = await adminService.verifyAdminUser(user.uid);
          currentAdminUser.value = adminUser;
        } catch (error) {
          console.error('Failed to verify admin status during initialization:', error);
          currentAdminUser.value = null;
        }
      } else {
        // No user authenticated
        currentAdminUser.value = null;
        adminError.value = null;
      }
      
      // Resolve after first auth state check
      unsubscribe();
      resolve();
    });
  });
}